import React, { useEffect, useState } from "react";
import Select from "react-select";
import PageHeader from "../../../components/common/PageHeader";
import { useParams } from "react-router-dom";
import { VERTICALS } from "../../../constants/verticals";
import { exportContactDirectoryReport, exportInquiryReport, getContactDirectoryReport, getInquiryReport } from "../api/reportService";
import { getChannelList, getClientSegmentByVertical, getInquirySourceList, getPLList, getProbabilityList, getResponsibleUsers } from "../../sales/inquiry/api/inquiry.api";
import { getClientList } from "../../client/api/clientService";
import "../styles/salesReport.css";
import { getStageList } from "../../stage";
import ListPagination from "../../../components/common/ListPagination";
import {
    DEFAULT_PAGE_SIZE,
    parsePaginationResponse,
} from "../../../components/common/paginationUtils";
type SelectOption = {
    label: string;
    value: number | string;
};

type ContactReportFiltersType = {
    date_from: string;
    date_to: string;
    source: SelectOption | null;
    vertical: SelectOption | null;
};
const formatDate = (dateString: string | null) => {
    if (!dateString) return "-";

    const date = new Date(dateString);

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
};
const ContactDirectoryReport: React.FC = () => {

    const { vertical } = useParams<{ vertical: string }>();

    const verticalConfig = VERTICALS.find(v => v.key === vertical);

    const verticalId = verticalConfig?.id;
    const verticalName = verticalConfig?.label || "";
    const [clientOptions, setClientOptions] = useState<any[]>([]);
    const [segmentOptions, setSegmentOptions] = useState<any[]>([]);
    const [plOptions, setPlOptions] = useState<any[]>([]);
    const [channelOptions, setChannelOptions] = useState<any[]>([]);
    const [responsibleOptions, setResponsibleOptions] = useState<any[]>([]);
    const [sourceOptions, setSourceOptions] = useState<any[]>([]);
    const [probabilityOptions, setProbabilityOptions] = useState<any[]>([]);
    const [dateError, setDateError] = useState<string>("");

    const [list, setList] = useState<any[]>([]);
    const [page, setPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [showingRecords, setShowingRecords] = useState("");
    const [pageInfo, setPageInfo] = useState("");
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [hasGenerated, setHasGenerated] = useState(false);
    const [stageOptions, setStageOptions] = useState<SelectOption[]>([]);
    const defaultFilters: ContactReportFiltersType = {
        date_from: "",
        date_to: "",
        source: null,
        vertical: null
    };
    const [filters, setFilters] = useState<ContactReportFiltersType>(defaultFilters);



    useEffect(() => {
        // Reset everything when vertical changes
        setFilters(defaultFilters);
        setList([]);
        setPage(1);
        setTotalCount(0);
        setShowingRecords("");
        setPageInfo("");

        setHasGenerated(false);
        setDateError("");
    }, [verticalId]);

    const [verticalOptions, setVerticalOptions] = useState<SelectOption[]>([]);

    useEffect(() => {
        setVerticalOptions(
            VERTICALS.map(v => ({
                label: v.label,
                value: v.key
            }))
        );
    }, []);
    useEffect(() => {
        const loadStages = async () => {
            try {
                const res = await getStageList(1, { is_active: true });

                setStageOptions(
                    res.data.results.map((s: any) => ({
                        label: s.stage_name,
                        value: s.id,
                    }))
                );
            } catch {
                console.error("Stage load failed");
            }
        };

        loadStages();
    }, []);
    const validateDates = () => {
        const { date_from, date_to } = filters;

        // Only validate if both exist
        if (date_from && date_to) {
            const from = new Date(date_from);
            const to = new Date(date_to);

            if (from > to) {
                setDateError("From Date cannot be greater than To Date");
                return false;
            }
        }

        setDateError("");
        return true;
    };

    useEffect(() => {
        const loadSources = async () => {
            try {
                const res = await getInquirySourceList();

                setSourceOptions(
                    res.data.results.map((s: any) => ({
                        label: s.name,
                        value: s.id,   // ✅ correct
                    }))
                );
            } catch {
                console.error("Source load failed");
            }
        };

        loadSources();
    }, []);
    useEffect(() => {
        if (!verticalId) return;

        const loadProbability = async () => {
            try {
                const res = await getProbabilityList();

                setProbabilityOptions(
                    res.data.results
                        .filter((p: any) => p.is_active)
                        .map((p: any) => ({
                            label: p.name,
                            value: p.id,
                        }))
                );
            } catch {
                console.error("Probability load failed");
            }
        };

        loadProbability();
    }, [verticalId]);


    const fetchReport = async (pageNo: number) => {
        const res = await getContactDirectoryReport(pageNo, {
            date_from: filters.date_from,
            date_to: filters.date_to,
            source: filters.source?.value || "",
            vertical: filters.vertical?.value || "",
            page_size: pageSize,
        });

        const pagination = parsePaginationResponse(res.data, {
            resultsKey: "auto",
            fallbackPageSize: pageSize,
            fallbackPage: pageNo,
        });

        setList(pagination.results);
        setTotalCount(pagination.totalCount);
        setShowingRecords(pagination.showingRecords);
        setPageInfo(pagination.pageInfo);
    };

    const handlePageSizeChange = (size: number) => {
        setPageSize(size);
        setPage(1);
    };

    useEffect(() => {
        if (hasGenerated) {
            fetchReport(page);
        }
    }, [page, pageSize, hasGenerated]);

    const handleExport = async () => {
        try {
            const res = await exportContactDirectoryReport({
                date_from: filters.date_from,
                date_to: filters.date_to,
                source: filters.source?.value || "",
                vertical: filters.vertical?.value || ""
            });

            const blob = new Blob([res.data], {
                type: "text/csv;charset=utf-8;",
            });

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "Contact_Directory_Report.csv";
            a.click();

            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Export failed", err);
        }
    };

    const handleClear = () => {
        setFilters(defaultFilters);
        setList([]);
        setPage(1);
        setTotalCount(0);
        setShowingRecords("");
        setPageInfo("");
        setHasGenerated(false);
    };
    const formatDate = (date?: string | null) => {
        if (!date) return "-";

        // already dd-mm-yyyy
        if (/^\d{2}-\d{2}-\d{4}$/.test(date)) {
            return date;
        }

        let d: Date;

        // handle MM/DD/YY or MM/DD/YYYY
        if (date.includes("/")) {
            const parts = date.split("/");

            let month = Number(parts[0]);
            let day = Number(parts[1]);
            let year = Number(parts[2]);

            // convert 2-digit year → 4-digit
            if (year < 100) {
                year += 2000;
            }

            d = new Date(year, month - 1, day);
        } else {
            // ISO or other format
            d = new Date(date);
        }

        if (isNaN(d.getTime())) return "-";

        const day = String(d.getDate()).padStart(2, "0");
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const year = d.getFullYear();

        return `${day}-${month}-${year}`;
    };
    return (
        <>
            <PageHeader
                title="Contact Directory Report"
                breadcrumbs={[
                    { label: "Dashboard", path: "/dashboard" },
                    { label: "Reports" },
                    { label: "Contact Directory" },
                ]}
            />

            <div className="container mt-4">
                {/* FILTER CARD */}
                <div className="card p-4">

                    {/* DATE */}
                    <div className="row mb-3">
                        <div className="col-md-3">
                            <label className="form-label fw-semibold my-1">From Date</label>
                            <div className="form-floating">
                                <input
                                    type="date"
                                    className="form-control"
                                    placeholder="From Date"
                                    value={filters.date_from}
                                    onChange={(e) =>
                                        setFilters(prev => ({
                                            ...prev,
                                            date_from: e.target.value,
                                        }))
                                    }
                                    max={filters.date_to || undefined}

                                />
                                <label>From Date</label>
                                {dateError && (
                                    <div className="text-danger mt-2">{dateError}</div>
                                )}
                            </div>
                        </div>

                        <div className="col-md-3">
                            <label className="form-label fw-semibold my-1">To Date</label>

                            <div className="form-floating">
                                <input
                                    type="date"
                                    className="form-control"
                                    placeholder="To Date"
                                    value={filters.date_to}
                                    min={filters.date_from || undefined}

                                    onChange={(e) =>
                                        setFilters(prev => ({
                                            ...prev,
                                            date_to: e.target.value,
                                        }))
                                    }
                                />
                                <label>To Date</label>
                            </div>
                        </div>




                        {/* FILTER GRID */}




                        {/* Source */}
                        <div className="col-md-3 stage-list">
                            <div className="form-group">
                                <label className="form-label fw-semibold my-1">Inquiry Source</label>
                                <Select
                                    options={sourceOptions}
                                    value={filters.source}
                                    onChange={(v) =>
                                        setFilters(prev => ({
                                            ...prev,
                                            source: v
                                        }))
                                    }
                                    placeholder="Source"
                                    classNamePrefix="react-select"
                                />
                            </div>
                        </div>
                        <div className="col-md-3 stage-list">
                            <div className="form-group">
                                <label className="form-label fw-semibold my-1">Vertical</label>
                                <Select
                                    options={verticalOptions}
                                    value={filters.vertical}
                                    onChange={(v) =>
                                        setFilters(prev => ({
                                            ...prev,
                                            vertical: v
                                        }))
                                    }
                                    placeholder="Select Vertical"
                                    classNamePrefix="react-select"
                                />
                            </div>
                        </div>






                    </div>

                    <div className="mt-4 d-flex align-item-end justify-content-end gap-2">

                        <button
                            className="btn btn-outline-secondary"
                            onClick={handleClear}
                        >
                            Clear
                        </button>

                        <button
                            className="btn btn-primary"
                            onClick={() => {
                                if (!validateDates()) return;

                                setPage(1);
                                setHasGenerated(true);
                                fetchReport(1);
                            }}


                        >
                            Generate
                        </button>
                    </div>

                </div>


                {/* TABLE */}
                <div className="card p-4 mt-4">
                    <div className="my-2 d-flex align-item-end justify-content-end gap-2">
                        <button
                            className="btn btn-primary"
                            onClick={handleExport}
                            disabled={!hasGenerated}
                        >
                            Export CSV
                        </button></div>
                    <div className="table-responsive">
                        <table className="table table-hover mb-0 report-table">
                            <thead>
                                <tr>
                                    <th>Source Name</th>
                                    <th>Contact Name</th>
                                    <th>Contact Number</th>
                                    <th>Email</th>
                                    <th>Company</th>
                                    <th>Status</th>
                                </tr>
                            </thead>

                            <tbody>
                                {list.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="text-center">
                                            No Data Found
                                        </td>
                                    </tr>
                                ) : (
                                    list.map((row, i) => (
                                        <tr key={i}>
                                            <td>{row.source_name}</td>
                                            <td>{row.contact_name}</td>
                                            <td>{row.contact_number}</td>
                                            <td>{row.email || "-"}</td>
                                            <td>{row.company_name || "-"}</td>
                                            <td>{row.status}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>


                        </table>
                    </div>

                    <ListPagination
                        page={page}
                        totalCount={totalCount}
                        pageSize={pageSize}
                        currentCount={list.length}
                        showingRecords={showingRecords}
                        pageInfo={pageInfo}
                        onPageChange={setPage}
                        onPageSizeChange={handlePageSizeChange}
                        visible={hasGenerated}
                        emptyMessage="No records found"
                        className="px-3"
                    />
                </div>

            </div>
        </>
    );
};

export default ContactDirectoryReport;